"use client";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import initializeThemeScripts from "../../Utils/initializeThemeScripts";
import PageHeaderSection from "../layout/PageHeaderSection";
import SEO from "../SEO";

interface JohnsHopkinsPanelistData {
  id: string;
  key: string;
  image: string;
}

const JohnsHopkinsPanelistDetailPage = () => {
  const { panelistId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [panelist, setPanelist] = useState<JohnsHopkinsPanelistData | null>(null);

  useEffect(() => {
    initializeThemeScripts();
    loadPanelistData();
  }, [panelistId, i18n.language]);

  const loadPanelistData = () => {
    if (!panelistId) return;

    // Get panelist data from predefined data
    const panelistData = getPanelistData(panelistId);
    if (panelistData) {
      setPanelist(panelistData);
    }
  };

  const getPanelistData = (id: string): JohnsHopkinsPanelistData | null => {
    const panelists = [
      {
        id: "1",
        key: "amyShelton",
        image: "/images/symposiums/John_Hopkins/Amy_Shelton.jpeg"
      },
      {
        id: "2",
        key: "kimberleyChandler",
        image: "/images/symposiums/John_Hopkins/Kimberley.jpg"
      },
      {
        id: "4",
        key: "triciaSchellenbach",
        image: "/images/symposiums/John_Hopkins/Tricia_Schellenb.jpg"
      }
    ];

    return panelists.find(p => p.id === id) || null;
  };

  if (!panelist) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <h3>{t('common.panelistNotFound', 'Panelist not found')}</h3>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate('/symposiums')}
          >
            {t('common.backToSymposiums', 'Back to Symposiums')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.name`)} - ${t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.title`)}`}
        description={t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.organization`)}
        canonicalUrl={`/johns-hopkins-panelist/${panelist.id}`}
        keywords={`panelist, gifted education, Johns Hopkins CTY, ${t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.name`)}`}
      />

      <PageHeaderSection
        title={t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.name`)}
        description={t("symposiumsPage.johnsHopkins.panelistHeader.foundation")}
      />

      <section className="small-section">
        <div className="container">
          {/* Panelist Biography Section */}
          <div className="row align-items-start mb-5">
            <div className="col-lg-2 col-md-4 text-center">
              <div className="d-inline-block position-relative">
                <img
                  src={panelist.image}
                  alt={t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.name`)}
                  className="rounded-circle w-150px h-150px mb-20px"
                  style={{
                    objectFit: "cover",
                    border: "3px solid #f0f0f0"
                  }}
                />
              </div>
            </div>
            <div className="col-lg-9 col-md-8">
              <h3 className="fw-700 text-dark-gray ls-minus-1px mb-3">
                {t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.name`)}
              </h3>
              <p className="fw-600 text-base-color mb-2">
                {t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.title`)}
              </p>
              <p className="text-medium-gray ls-minus-1px mb-3">
                <em>{t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.organization`)}</em>
              </p>
              
              <div className="mb-3">
                <h5 className="fw-600 text-dark-gray ls-minus-1px mb-2">
                  {t("symposiumsPage.johnsHopkins.panelistDetail.about")}
                </h5>
                <p className="text-medium-gray ls-minus-1px">
                  {t(`symposiumsPage.johnsHopkins.panelistDetail.${panelist.key}.bio`)}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default JohnsHopkinsPanelistDetailPage;
